import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/KStackAuthContext';
import ThemeToggle from '../components/ThemeToggle';
import './Login.css';

/**
 * Forgot-password page (Phase F4, 2026-05-25).
 *
 * Posts to Nexus's POST /auth/forgot-password. Nexus is anti-enum:
 * the server ALWAYS responds 200 regardless of whether the email
 * exists, so the SPA can't be used as an account-enumeration
 * oracle. We mirror that posture in the UI — the message shown
 * after submit is the same whether or not an OTP was actually sent.
 *
 * After submit we navigate to /reset-password with the email pre-
 * filled, since the immediate next step is entering the 6-digit
 * code + a new password.
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const navigate = useNavigate();
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email) {
      setErrorMsg('Enter the email associated with your account.');
      return;
    }

    setIsLoading(true);
    await forgotPassword(email);
    // Always navigate, regardless — Nexus is anti-enum. The reset
    // page surfaces the "if-an-account-exists" copy and accepts the
    // OTP if the email turns out to be real.
    navigate('/reset-password', {
      state: {
        email,
        info:
          'If an account exists for that email, a 6-digit reset code is on its way. Enter it below with your new password.',
      },
    });
  };

  return (
    <div className="login-container">
      <ThemeToggle placement="corner" />
      <div className="login-box">
        <div className="login-header">
          <h1>Forgot password?</h1>
        </div>
        <p>
          Enter the email you signed up with — we'll send you a 6-digit reset
          code.
        </p>

        {errorMsg && (
          <div className="error-modal" onClick={() => setErrorMsg(null)}>
            <div className="error-content" onClick={(e) => e.stopPropagation()}>
              <div className="error-message">❌ {errorMsg}</div>
              <button
                className="error-ok-button"
                onClick={() => setErrorMsg(null)}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          </div>

          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? 'Sending…' : 'Send reset code'}
          </button>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <Link to="/login" className="btn-secondary-link">
          Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
